import Dialog from '@reach/dialog';
import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { toast } from 'react-toastify';

import AlertModal from '../../components/AlertModal';
import CopyLink from '../../components/CopyLink';
import GenericPopover from '../../components/GenericPopover';
import GenericTable from '../../components/GenericTable';
import { Header } from '../../components/Header';
import LoaderButton from '../../components/LoaderButton';
import { Loading } from '../../components/Loading';

import { UserContextInterface, useUser } from '../../context/User';
import { useTRRs } from '../../context/ToolRecoveryRequests';
import { useTaxExemptRequests } from '../../context/TaxExemptRequests';
import { toolRecoveryRequestsColumns } from '../../libs/tables/columnDefinitions';
import { getSavedColumns } from '../../libs/utils-ts';
import { ChildProps, TaxExemptRequest, ToolRecoveryRequest } from '../../types';
import CustomDatePicker, { DateRangeOptions } from '../../components/CustomDatePicker';
import { calculateStartAndEndDate, formatTimezone2Lines } from '../../libs/utils';
import ToolRecoveryModal from '../../components/ToolRecoveryModal';
import TaxExemptModal from '../../components/TaxExemptModal';
import HelpTooltip from '../../components/Tooltips/HelpTooltip';
import ConfirmModal from '../../components/ConfirmModal';
import { TextOnly } from '../../components/Text';
import Tabs from '../../components/Tabs';
import Tab from '../../components/Tab';
import BadToolSerials from './BadToolSerials';

const STATUS_ORDER = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
};
interface HomeProps extends ChildProps {
  history: any;
  userContext: UserContextInterface;
  handleShowAlert: (
    alertTitle: string,
    alertMessage: string | JSX.Element,
    stringify?: boolean
  ) => void;
}

const Dashboard: React.FC<HomeProps> = ({history, userContext, handleShowAlert}) => {
  const { user } = useUser();
  const { areTRRsLoading, toolRecoveryRequests, toolRecoveryShops, handleRefresh, handleContactOwner, handleProcessRequest, handleCloseRequest, handleTransferDisputeInfo } = useTRRs();
  const { areTaxExemptRequestsLoading, taxExemptRequests, exemptionReasons, taxExemptStatus, setTaxExemptStatus, setTaxExemptDateRange, handleRefreshTaxExempt, handleApproveRequest, handleRejectRequest } = useTaxExemptRequests();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [recoveryRequests, setRecoveryRequests] = useState<any[]>([]);
  const [recoveryShops, setRecoveryShops] = useState<any>({} as { [k: string]: any });
  const [selectedRequest, setSelectedRequest] = useState<ToolRecoveryRequest>();
  const [isProcessingRequest, setIsProcessingRequest] = useState<boolean>(false);
  const [recoveryRejectReason, setRecoveryRejectReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [showRejectReasonModal, setShowRejectReasonModal] = useState<boolean>(false);
  const [loadingActions, setLoadingActions] = useState<string>('');
  const [showClosed, setShowClosed] = useState<boolean>(false);
  const [rangePicked, setRangePicked] = useState<DateRangeOptions>('last30Days');
  const [showRecoveryModal, setShowRecoveryModal] = useState<boolean>(false);
  const [toolRecoveryRequest, setToolRecoveryRequest] = useState<any>({});
  const [showDisputeTransferModal, setShowDisputeTransferModal] = useState<boolean>(false);
  const [recoveryRequestWithDispute, setRecoveryRequestWithDispute] = useState<any>(null);

  // Tax Exempt Requests state
  const [selectedTaxExemptRequest, setSelectedTaxExemptRequest] = useState<TaxExemptRequest | null>(null);
  const [showTaxExemptModal, setShowTaxExemptModal] = useState<boolean>(false);
  const [taxExemptActionType, setTaxExemptActionType] = useState<'APPROVE' | 'REJECT' | 'BOTH'>('BOTH');
  const [loadingTaxExemptActions, setLoadingTaxExemptActions] = useState<string>('');
  const [taxExemptRangePicked, setTaxExemptRangePicked] = useState<DateRangeOptions>('last30Days');

  useEffect(() => {
    window.addEventListener('resize', handleSetWindowWidth);
    return () => {
      window.removeEventListener('resize', handleSetWindowWidth);
    };
  }, []);

  useEffect(() => {
    setRecoveryRequests(toolRecoveryRequests ?? []);
  }, [toolRecoveryRequests]);

  useEffect(() => {
    setRecoveryShops(toolRecoveryShops ?? []);
  }, [toolRecoveryShops]);

  // Sync date range changes to context when user picks a new range
  useEffect(() => {
    const { startDateStr, endDateStr } = calculateStartAndEndDate(taxExemptRangePicked);
    setTaxExemptDateRange(startDateStr, endDateStr);
  }, [taxExemptRangePicked]);

  const handleSetWindowWidth = () => {
    setTimeout(() => {
      setWindowWidth(window.outerWidth);
    }, 20);
  };

  const handleRadioChange = (e: any) => {
    setRecoveryRejectReason(e.target.value);
    setOtherReason('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (recoveryRejectReason === 'other') {
      setOtherReason(e.target.value);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleCancelRejectRecovery = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    let self = this;
    setTimeout(() => {
      setSelectedRequest(undefined);
      setIsProcessingRequest(false);
      setRecoveryRejectReason('');
      setShowRejectReasonModal(false);
      setShowRecoveryModal(false);
    }, 350);
  };

  const handleCancelRecoveryModal = () => {
    setShowRecoveryModal(false);
    setSelectedRequest(undefined);
    setIsProcessingRequest(false);
    setShowRejectReasonModal(false);
  }

  const handleShowRejectReasonModal = () => {
    setSelectedRequest(toolRecoveryRequest);
    setShowRejectReasonModal(true)
  }

  const refreshTRRs = async () => {
    setIsLoading(true);

    const { startDateStr, endDateStr } = calculateStartAndEndDate(rangePicked);

    const response = await handleRefresh(true, showClosed, startDateStr, endDateStr);

    if (Object.hasOwn(response ?? {}, 'error')) {
      if ([401, 403].indexOf(response.error.status) === -1) {
        handleShowAlert(
          'Error handling request',
          response.error
        );
      }
    }

    setIsLoading(false);
    return;
  }

  const contactOwner = async (toolID: string) => {
    setLoadingActions(toolID);

    const response = await handleContactOwner(toolID);

    let modalTitle;
    let modalMsg;

    if (!response.error) {
      modalTitle = "Success!";
      modalMsg = <p>
        Owner has been contacted, and a Zendesk ticket has been opened for
        them. View it by clicking on the ticket{' '}
        <span className="c-btn__icon fa fa-ticket" title="Zendesk Ticket" />{' '}
        icon under the Zendesk column for this request.
      </p>;
    } else {
      modalTitle = 'Error handling request';
      if ([401, 403].indexOf(response.error.status) === -1) {
        modalMsg = <p>{response.error}</p>
      } else {
        modalMsg = <p>{response}</p>
      }
    }

    handleShowAlert(modalTitle, modalMsg, false);
    setLoadingActions('');
    setShowRecoveryModal(false);
    return;
  }

  const processRequest = async (toolID: string, approved: boolean, reason?: string) => {
    setLoadingActions(toolID);
    setIsLoading(true);

    const response = await handleProcessRequest(toolID, approved, reason);

    if (!response.error) {
      if (approved) {
        handleShowAlert(
          'Success!',
          response.message === 'Tool not removed' ? (
            'Tool was previously removed and is now available to add.  Tickets closed.'
          ) : response.message === 'Tool is owned by a different shop' ? (
            <p>
              Tool was previously removed but is now owned by another shop.
              <CopyLink
                content={response.toolOwnership.shopID}
                type={'SHOP ID'}
                redirect={'/userFunctions/getShopSummary'}
                urlParams={`?query=${response.toolOwnership.shopID}&queryType=SHOP+ID`}
                action="search"
              />{' '}
              Tickets closed.
            </p>
          ) : (
            'Tool has been removed from this shop!'
          ),
          false
        )
      } else {
        handleShowAlert(
          'Success!',
          'Tool recovery request has been rejected!',
          false
        );
      }
    } else {
      handleShowAlert('Error handling request', response.error);
    }

    setSelectedRequest(undefined);
    setIsProcessingRequest(false);
    setShowRejectReasonModal(false);
    setShowRecoveryModal(false);
    setLoadingActions('');
    setShowRecoveryModal(false);
    setIsLoading(false);
  }

  const closeRequest = async (toolID: string, reason: string) => {
    setIsLoading(true);
    setLoadingActions(toolID);

    const response = await handleCloseRequest(toolID, reason);

    if (!response.error) {
      handleShowAlert(
        'Success!',
        'Tool recovery request has been rejected, and ticket has been closed!',
        false
      );
    } else {
      handleShowAlert('Error handling request', response.error)
    }

    setSelectedRequest(undefined);
    setLoadingActions('');
    setIsLoading(false);
    setShowRejectReasonModal(false);
    setRecoveryRejectReason('');
    setShowRecoveryModal(false);
  }

  const transferDisputeInfo = async () => {
    setIsLoading(true);
    const response = await handleTransferDisputeInfo(recoveryRequestWithDispute.toolID);
    if (response?.error) {
      toast.error(`${TextOnly('errorTransferringDisputeInfo')}: ${response.error}`, { containerId: 'standard' });
    } else {
      toast.success(TextOnly('successfullyTransferredDisputeInfo'), { containerId: 'standard' });
    }
    setIsLoading(false);
  }

  // Functions to handle approve/reject from the TaxExemptModal
  const handleApproveFromModal = async (teRequestID: string, userID: string, reason: string, zendeskTicketID: string, skipRefunds: boolean) => {
    setLoadingTaxExemptActions(teRequestID);
    const matchingReason = exemptionReasons.find(r => r.name === reason);
    const response = await handleApproveRequest(teRequestID, userID, reason, matchingReason?.id ?? '', zendeskTicketID, skipRefunds);

    if (!response?.error) {
      let extraText = '';
      if (response !== reason) {
        extraText = ` with ${response}`;
      }
      if (response !== reason) {
        toast.warning(`Success! Tax exempt request has been approved${extraText}. An email has been sent to the owner with the signup link.`, { containerId: 'wide-toast' });
      } else { 
        toast.success(`Success! Tax exempt request has been approved. An email has been sent to the owner with the signup link.`, { containerId: 'wide-toast' });
      }
    } else {
      handleShowAlert('Error handling request', response?.error);
    }

    setLoadingTaxExemptActions('');
    return response;
  };

  const handleRejectFromModal = async (teRequestID: string, userID: string, reason: string, zendeskTicketID: string) => {
    setLoadingTaxExemptActions(teRequestID);

    const response = await handleRejectRequest(teRequestID, userID, reason, zendeskTicketID);

    if (!response?.error) {
      toast.success('Success! Tax exempt request has been rejected. An email has been sent to the owner with the reason.', { containerId: 'standard' });
    } else {
      handleShowAlert('Error handling request', response?.error);
    }

    setLoadingTaxExemptActions('');
    return response;
  };

  const rejectionReasonPhrases = {
    wrongToolSerialNumber: `You have registered something other than the serial number.  Please register the serial number, normally located below a barcode on the back of the unit, or above a QR code.`,
    pixelatedPhoto: `Please re-register your tool and submit a new recovery request at https://webapp.autoauth.com under "Manage Tools", as this one is too pixelated to make out.`,
    photoOfPhoto: `Please re-register your tool and submit a new recovery request at https://webapp.autoauth.com under "Manage Tools" with a direct photo of the tool, not a photo of a photo.`,
    serialMismatch: `The serial number in the photo does not match the serial number you've registered. Please check again and re-submit your request at https://webapp.autoauth.com.`
  }

  const rejectionMessage =
    <div>
      <div className="u-margin-bottom-large">
        Your request to recover the tool with serial: {selectedRequest?.toolSerial} has been rejected.
      </div>
      <div><strong>Reason for Rejection:</strong></div>
      <div className="u-margin-bottom-large">
        {recoveryRejectReason === 'other' ? otherReason : rejectionReasonPhrases[recoveryRejectReason]}
      </div>
      <div>
        If you would like to dispute this decision, please contact support. This ticket will now be closed.
      </div>
    </div>
  ;

  return !user ?
    <Loading />
    :
    <main>
      <Header title="Dashboard" />

      <ToolRecoveryModal
          showModal={showRecoveryModal || false}
          handleCancel={handleCancelRecoveryModal}
          toolRecoveryRequest={toolRecoveryRequest}
          toolRecoveryShops={toolRecoveryShops}
          contactOwner={contactOwner}
          loadingActions={loadingActions}
          processRequest={processRequest}
          handleShowRejectModal={handleShowRejectReasonModal}
          setSelectedRequest={setSelectedRequest}
          setIsProcessingRequest={setIsProcessingRequest}
          setShowRejectReasonModal={setShowRejectReasonModal}
        />

      <TaxExemptModal
        showModal={showTaxExemptModal}
        handleCancel={() => {
          setShowTaxExemptModal(false);
          setSelectedTaxExemptRequest(null);
        }}
        taxExemptRequest={selectedTaxExemptRequest}
        handleApprove={handleApproveFromModal}
        handleReject={handleRejectFromModal}
        loadingActions={loadingTaxExemptActions}
        exemptionReasons={exemptionReasons}
        actionType={taxExemptActionType}
      />

      {['ADMIN', 'MANAGER'].includes(user.userType) &&
        <Tabs>
          <Tab label={'Tool Recovery Requests'}>
            <div>
              <div className="l-flex-between l-flex-align-center">
                <h2 className="c-card__title">Tool Recovery Requests</h2>
                <div className='l-flex-align-center'>
                  <div className="c-btn__inner l-margin-right-10">Show Closed</div>
                  <input
                    id="showClosed"
                    type="checkbox"
                    name="showClosed"
                    checked={showClosed}
                    onChange={()=> setShowClosed(!showClosed)}
                    className="c-btn__inner l-margin-right-20"
                  />
                  <button
                    className="c-btn-icon"
                    onClick={() => refreshTRRs()}
                  >
                    <div className="c-btn__inner">
                      <span className="c-btn__icon fa fa-refresh" title="Approve" />
                    </div>
                  </button>
                </div>
              </div>

              <hr />
              <div className="c-field">
                <label
                  htmlFor="query"
                  className="c-field__label u-margin-top-large"
                >
                  Date Range
                </label>
                <CustomDatePicker
                  rangePicked={rangePicked}
                  setRangePicked={(newRange: DateRangeOptions) => setRangePicked(newRange)}
                />
              </div>
              <GenericTable
                data={recoveryRequests || []}
                filterKeys={[
                  'actionMessage',
                  'actionCode',
                  'actionDate',
                  'toolModel',
                  'toolManufacturer',
                  'toolSerial',
                ]}
                columnDefs={[
                  ...toolRecoveryRequestsColumns.slice(0, 2),
                  {
                    Header: 'Requester Shop',
                    accessor: 'shopID',
                    Cell: (row: any) => {
                      const disputeTransferred = row.original.disputeInfoTransferred;
                      const disputePaid = row.original.disputePaid;
                      return (
                        <div>
                          <CopyLink
                            content={row.original.shopID}
                            sub={recoveryShops[row.original.shopID]}
                            className="u-font-mono"
                            type="SHOP ID"
                            redirect="/shopFunctions/getShopSummary"
                            showCopy={false}
                            urlParams={`?query=${row.original.shopID}&queryType=SHOP+ID`}
                          />
                          { disputePaid ? (
                            <span className="u-text-success">(Repaid Dispute)</span>
                          ) : disputeTransferred ? (
                            <span className="u-text-warning">(Dispute Repayment Pending)</span>
                          ) : ''}
                        </div>
                      );
                    },
                  },
                  {
                    Header: 'Tool Owner Shop',
                    accessor: 'ownerShopID',
                    Cell: (row: any) => {
                      if (!row.original.ownerShopID) return null;
                      const lostDispute = row.original.ownerShopWeLostDispute;
                      const disputeTransferred = row.original.disputeInfoTransferred;
                      const disputePaid = row.original.disputePaid;
                      return (
                        row.original.ownerShopID && (
                          <div>
                            <CopyLink
                              content={row.original.ownerShopID}
                              sub={recoveryShops[row.original.ownerShopID]}
                              className="u-font-mono"
                              type="SHOP ID"
                              redirect="/shopFunctions/getShopSummary"
                              showCopy={false}
                              urlParams={`?query=${row.original.ownerShopID}&queryType=SHOP+ID`}
                            />
                            {
                              row.original.isEnterprise ?
                                <span className="u-text-warning">(Enterprise Shop)</span>
                              : (lostDispute && !disputeTransferred && !disputePaid) ?
                                ['ADMIN', 'MANAGER'].includes(user.userType) ? (
                                  <button
                                    className="c-btn-medium c-btn-warning"
                                    onClick={() => {
                                      setShowDisputeTransferModal(true);
                                      setRecoveryRequestWithDispute(row.original);
                                    }}
                                  >
                                    Lost Dispute
                                  </button>
                                ) : (
                                  <span className="u-text-warning">(Lost Dispute)</span>
                                )
                              : (lostDispute && disputeTransferred && disputePaid) ?
                                <span className="u-text-success">(Dispute Resolved)</span>
                              : ''
                            }
                          </div>
                        )
                      );
                    },
                  },
                  ...toolRecoveryRequestsColumns.slice(2,6),
                  ...toolRecoveryRequestsColumns.slice(7),
                  {
                    Header: 'Serial Detected',
                    accessor: 'serialDetected',
                    minWidth: 120,
                    maxWidth: 120,
                    Cell: (row: {original: any }) => {
                      if (row.original.serialDetected === true) {
                        return (
                          <HelpTooltip
                            label={"Serial detected on image"}
                            child={<span className="c-btn__icon fa fa-check icon-success" title="Serial detected on image" />}
                          />)

                      } else if (row.original.serialDetected === false) {
                        return (
                          <HelpTooltip
                            label={"Serial not detected on image"}
                            child={<span className="c-btn__icon fa fa-question icon-warning" title="Serial not detected on image" />}
                          />)
                      } else {
                        return null;
                      }
                    }
                  },
                  {
                    Header: 'Image',
                    accessor: 'docKey',
                    minWidth: 50,
                    maxWidth: 50,
                    Cell: (row: { original: any }) => {
                      if (row.original.docKey && (row.original.imageRemovalNeeded || !row.original.status.includes('CLOSED'))) {
                        return (
                          <button
                            className="c-btn-icon"
                            onClick={() =>
                              {
                                setShowRecoveryModal(true);
                                setToolRecoveryRequest(row.original);
                              }
                            }
                          >
                            <div className="c-btn__inner">
                              <span className="c-btn__icon fa fa-image" title="Image URL" />
                            </div>
                          </button>
                        );
                      } else {
                        return null;
                      }
                    },
                  },
                  {
                    Header: 'Actions',
                    accessor: 'userID',
                    minWidth:
                      getSavedColumns('toolRecoveryRequestsColumns').userID ||
                      200,
                    maxWidth: 200,
                    Cell: (row: { original: any }) => {
                      return row.original.status.includes('CLOSED') ? (
                        <div className="l-display-block">
                          <div>
                            { row.original.status }
                          </div>
                          <div>
                            { row.original.closeReason }
                          </div>
                        </div>

                      ) : row.original.status.includes("PENDING") ? (
                        <div className="l-inline-flex">
                          {loadingActions !== row.original.toolID ? (
                            <>
                              <GenericPopover
                                anchorElement={
                                  <button className="c-btn-icon--green">
                                    <div className="c-btn__inner">
                                      <span
                                        className="c-btn__icon fa fa-check"
                                        title="Approve Request"
                                      />
                                    </div>
                                  </button>
                                }
                                body={
                                  <strong>
                                    Are you sure you want to approve this request?
                                  </strong>
                                }
                                primaryBtnElement={
                                  <LoaderButton
                                    className="c-btn-medium c-btn-success"
                                    disabled={false}
                                    text="Yes, approve it"
                                    loadingText="Approving..."
                                    icon={'fa fa-check'}
                                  />
                                }
                                secondaryBtnElement={
                                  <button className="c-btn-medium c-btn-outline">
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fal fa-times" />
                                      No, not yet
                                    </div>
                                  </button>
                                }
                                primaryAction={ async () => {
                                  return processRequest(row.original.toolID, true);
                                }}
                                color="#0088cc"
                                position="top"
                                align="end"
                                arrowSize={5}
                                closeOnClick={false}
                              />

                              <GenericPopover
                                anchorElement={
                                  <button className="c-btn-icon--red">
                                    <div className="c-btn__inner">
                                      <span
                                        className="c-btn__icon fa fa-times"
                                        title="Deny Request"
                                      />
                                    </div>
                                  </button>
                                }
                                body={
                                  <strong>
                                    Are you sure you want to deny this request?
                                  </strong>
                                }
                                primaryBtnElement={
                                  <LoaderButton
                                    className="c-btn-medium c-btn-error"
                                    disabled={false}
                                    text="Yes, deny it"
                                    loadingText="Denying..."
                                    icon={'fa fa-check'}
                                  />
                                }
                                secondaryBtnElement={
                                  <button className="c-btn-medium c-btn-outline">
                                    <div className="c-btn__inner">
                                      <i className="c-btn__icon fal fa-times" />
                                      No, not yet
                                    </div>
                                  </button>
                                }
                                primaryAction={ async () => {
                                  setSelectedRequest(row.original);
                                  setIsProcessingRequest(true);
                                  setShowRejectReasonModal(true);
                                }}
                                color="#0088cc"
                                position="top"
                                align="end"
                                arrowSize={5}
                                closeOnClick={false}
                              />
                            </>
                          ) : (
                            <button className="c-btn-icon">
                              <div className="c-btn__inner">
                                <i className="c-btn__icon fal fa-spinner-third spinning" />
                              </div>
                            </button>
                          )}
                        </div>
                      ) : loadingActions !== row.original.toolID ? (
                        <div className="l-inline-flex">
                          <GenericPopover
                            anchorElement={
                              <button className="c-btn-icon">
                                <div className="c-btn__inner">
                                  <span
                                    className="c-btn__icon fa fa-envelope"
                                    title="Contact Owner"
                                  />
                                </div>
                              </button>
                            }
                            body={
                              <strong>
                                Are you sure you want to open a ticket with this
                                tool's owner?
                              </strong>
                            }
                            primaryBtnElement={
                              <LoaderButton
                                className="c-btn-medium c-btn-success"
                                disabled={false}
                                text="Yes, open it"
                                loadingText="Opening..."
                                icon={'fa fa-check'}
                              />
                            }
                            secondaryBtnElement={
                              <button className="c-btn-medium c-btn-outline">
                                <div className="c-btn__inner">
                                  <i className="c-btn__icon fal fa-times" />
                                  No, not yet
                                </div>
                              </button>
                            }
                            primaryAction={ async () => {
                              return contactOwner(row.original.toolID);
                            }}
                            color="#0088cc"
                            position="top"
                            align="end"
                            arrowSize={5}
                            closeOnClick={false}
                          />

                          <GenericPopover
                            anchorElement={
                              <button className="c-btn-icon">
                                <div className="c-btn__inner">
                                  <span
                                    className="c-btn__icon fa fa-trash-alt"
                                    title="Close Request"
                                  />
                                </div>
                              </button>
                            }
                            body={
                              <strong>
                                Are you sure you want to close this request?
                              </strong>
                            }
                            primaryBtnElement={
                              <LoaderButton
                                className="c-btn-medium c-btn-success"
                                disabled={false}
                                text="Yes, close it"
                                loadingText="Closing..."
                                icon={'fa fa-check'}
                              />
                            }
                            secondaryBtnElement={
                              <button className="c-btn-medium c-btn-outline">
                                <div className="c-btn__inner">
                                  <i className="c-btn__icon fal fa-times" />
                                  No, not yet
                                </div>
                              </button>
                            }
                            primaryAction={ async () => {
                              setSelectedRequest(row.original);
                              setShowRejectReasonModal(true);
                            }}
                            color="#0088cc"
                            position="top"
                            align="end"
                            arrowSize={5}
                            closeOnClick={false}
                          />
                        </div>
                      ) : (
                        <button className="c-btn-icon">
                          <div className="c-btn__inner">
                            <i className="c-btn__icon fal fa-spinner-third spinning" />
                          </div>
                        </button>
                      );
                    },
                  },
                ]}
                defaultSorted={[
                  {
                    id: 'actionDate',
                    desc: true,
                  },
                ]}
                loadingText={'Loading requests...'}
                loading={areTRRsLoading}
                savedColumnsId={'toolRecoveryRequestsColumns'}
              />
            </div>
          </Tab>
          <Tab label={'Blocked Tool Serials'}>
            <BadToolSerials handleShowAlert={handleShowAlert} />
          </Tab>
          {user.userType === 'ADMIN' &&
            <Tab label={'Tax Exempt Requests'}>
              <div>
                <div className="l-flex-between l-flex-align-center">
                  <h2 className="c-card__title">Tax Exempt Requests</h2>
                  <div className='l-flex-align-center'>
                    <div className="c-btn__inner l-margin-right-10">Show Closed</div>
                    <input
                      id="showClosedTaxExempt"
                      type="checkbox"
                      name="showClosedTaxExempt"
                      checked={taxExemptStatus === null}
                      onChange={() => setTaxExemptStatus(taxExemptStatus === null ? 'PENDING' : null)}
                      className="c-btn__inner l-margin-right-20"
                    />
                    <button
                      className="c-btn-icon"
                      onClick={() => {
                        // Recalculate start and end date since endDate is the current moment not just the date
                        const { startDateStr, endDateStr } = calculateStartAndEndDate(taxExemptRangePicked);
                        setTaxExemptDateRange(startDateStr, endDateStr);
                        handleRefreshTaxExempt(startDateStr, endDateStr)
                      }}
                    >
                      <div className="c-btn__inner">
                        <span className="c-btn__icon fa fa-refresh" title="Refresh" />
                      </div>
                    </button>
                  </div>
                </div>

                <hr />

                <div className="c-field">
                  <label
                    htmlFor="query"
                    className="c-field__label u-margin-top-large"
                  >
                    Date Range
                  </label>
                  <CustomDatePicker
                    rangePicked={taxExemptRangePicked}
                    setRangePicked={(newRange: DateRangeOptions) => setTaxExemptRangePicked(newRange)}
                  />
                </div>

                <GenericTable
                  data={taxExemptRequests || []}
                  filterKeys={[
                    'shopName',
                    'email',
                    'taxpayerID',
                    'createdOn',
                  ]}
                  columnDefs={[
                    {
                      Header: 'Status',
                      accessor: 'status',
                      minWidth: 100,
                      maxWidth: 100,
                      sortMethod: (a, b) => {
                        return STATUS_ORDER[a] - STATUS_ORDER[b];
                      },
                      Cell: (row: { original: TaxExemptRequest }) => {
                        const statusColors: { [key: string]: string } = {
                          PENDING: 'icon-warning',
                          APPROVED: 'icon-success',
                          REJECTED: 'u-text-error',
                        };
                        return (
                          <span className={`u-font-bold ${statusColors[row.original.status] || ''}`}>
                            {row.original.status}
                          </span>
                        );
                      },
                    },
                    {
                      Header: 'Created On',
                      accessor: 'createdOn',
                      minWidth: 130,
                      maxWidth: 130,
                      Cell: (row: { original: TaxExemptRequest }) => {
                        let dateObj = formatTimezone2Lines(row.original.createdOn);
                        return (
                          <>
                            <div>
                              {dateObj.date}
                              <br />
                              {dateObj.time} {dateObj.abbr}
                            </div>
                          </>
                        );
                      },
                    },
                    {
                      Header: 'Shop Name',
                      accessor: 'shopName',
                      minWidth: 150,
                      Cell: (row: any) => {
                        return (row.original.shopID ? <CopyLink
                          content={row.original.shopName}
                          type={'SHOP ID'}
                          redirect={'/shopFunctions/getShopSummary'}
                          urlParams={`?query=${row.original.shopID}&queryType=SHOP+ID`}
                          action="search"
                        />: <span>{row.original.shopName}</span>)
                      }
                    },
                    {
                      Header: 'Shop Address',
                      accessor: 'address',
                      minWidth: 200,
                      Cell: (row: any) => {
                        const addr = row.original.address;
                        return (
                          <span>{addr.streetAddress1}{addr.streetAddress2 ? `, ${addr.streetAddress2}` : ''}, {addr.city}, {addr.state} {addr.zip}</span>
                        );
                      },
                    },
                    {
                      Header: 'Owner Email',
                      accessor: 'email',
                      minWidth: 200,
                      Cell: (row: any) => {
                        return (
                          <CopyLink
                            content={row.original.email}
                            type={'EMAIL'}
                            showCopy={false}
                          />
                        );
                      },
                    },
                    {
                      Header: 'Zendesk',
                      accessor: 'zendeskTicketID',
                      minWidth: 65,
                      maxWidth: 65,
                      Cell: (row: any) => {
                        return (
                          <button 
                            className="c-btn-icon" 
                            onClick={() => { window.open(
                                `https://autoauth.zendesk.com/agent/tickets/${row.original.zendeskTicketID}`,
                                '_blank') }
                            }
                          >
                            <div className="c-btn__inner">
                              <span
                                className="c-btn__icon fa fa-ticket"
                                title="Zendesk Ticket"
                              />
                            </div>
                          </button>
                        )
                      }
                    },
                    {
                      Header: 'Tax Certificate',
                      accessor: 'docKey',
                      minWidth: 120,
                      maxWidth: 120,
                      Cell: (row: { original: TaxExemptRequest }) => {
                        if (row.original.docKey) {
                          return (
                            <button
                              className="c-btn-icon"
                              onClick={() => {
                                setSelectedTaxExemptRequest(row.original);
                                setTaxExemptActionType('BOTH');
                                setShowTaxExemptModal(true);
                              }}
                            >
                              <div className="c-btn__inner">
                                <span className="c-btn__icon fa fa-image" title="View Tax Certificate" />
                              </div>
                            </button>
                          );
                        }
                        return null;
                      },
                    },
                    {
                      Header: 'Reason',
                      accessor: 'teRequestID',
                      minWidth: 150,
                      maxWidth: 150,
                      Cell: (row: { original: TaxExemptRequest }) => {
                        // Show exemption/rejection reason for approved/rejected requests
                        if (row.original.status === 'APPROVED') {
                          return (
                            <span className="u-text-success" title={row.original.exemptionReason}>
                              {row.original.exemptionReason}
                            </span>
                          );
                        }

                        if (row.original.status === 'REJECTED') {
                          return (
                            <span className="u-text-error" title={row.original.rejectionReason}>
                              {row.original.rejectionReason}
                            </span>
                          );
                        }

                        // Only show action buttons for PENDING requests
                        if (row.original.status === 'PENDING') {
                          return null;
                        }

                        // return (
                        //   <div className="l-inline-flex">
                        //     {loadingTaxExemptActions !== row.original.teRequestID ? (
                        //       <>

                        //         <button className="c-btn-icon--green" onClick={async () => {
                        //           setSelectedTaxExemptRequest(row.original);
                        //           setTaxExemptActionType('APPROVE');
                        //           setShowTaxExemptModal(true);
                        //         }}>
                        //           <div className="c-btn__inner">
                        //             <span
                        //               className="c-btn__icon fa fa-check"
                        //               title="Approve Request"
                        //             />
                        //           </div>
                        //         </button>


                        //         <button className="c-btn-icon--red" onClick={async () => {
                        //           setSelectedTaxExemptRequest(row.original);
                        //           setTaxExemptActionType('REJECT');
                        //           setShowTaxExemptModal(true);
                        //         }}>
                        //           <div className="c-btn__inner">
                        //             <span
                        //               className="c-btn__icon fa fa-times"
                        //               title="Reject Request"
                        //             />
                        //           </div>
                        //         </button>
                        //       </>
                        //     ) : (
                        //       <button className="c-btn-icon">
                        //         <div className="c-btn__inner">
                        //           <i className="c-btn__icon fal fa-spinner-third spinning" />
                        //         </div>
                        //       </button>
                        //     )}
                        //   </div>
                        // );
                      },
                    },
                  ]}
                  defaultSorted={[
                    {
                      id: 'createdOn',
                      desc: true,
                    },
                  ]}
                  loadingText={'Loading tax exempt requests...'}
                  loading={areTaxExemptRequestsLoading}
                  savedColumnsId={'taxExemptRequestsColumns'}
                />
              </div>
            </Tab>
          }
        </Tabs>
      }

      <AlertModal
        title={alertTitle}
        message={alertMessage}
        showModal={showModal}
        handleCancel={() => handleCancel()}
      />

      {/* Tool Recovery Reject Reason */}
      <Dialog
        isOpen={showRejectReasonModal}
        onDismiss={() => handleCancelRejectRecovery()}
        className="c-modal"
        aria-label={'Confirm Reject Recovery'}
      >
        <button
          className="c-btn-icon c-modal__close"
          onClick={() => handleCancelRejectRecovery()}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fa fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Confirm Recovery Rejection</h1>

        <div className="c-modal__body">
          <p className="u-margin-bottom-xlarge">
            Please specify a reason for rejecting this recovery. Note that if
            you type in an "Other" reason, the requester will be able to see
            your response in the ticket. If no reason is specified, the
            default rejection message will be sent to the requester.
          </p>

          <div className="u-margin-bottom-xlarge">
            <div className="c-field">
              <label className="c-field__label u-margin-bottom-small">Select a reason</label>
              <div onChange={handleRadioChange}>
                <div className="l-flex u-align-center u-margin-bottom-large">
                  <input
                    type="radio"
                    id="radioGroup"
                    name="radioGroup"
                    value="wrongToolSerialNumber"
                  />
                  <div>
                    Something other than the serial number
                  </div>
                </div>

                <div className="l-flex u-align-center u-margin-bottom-large">
                  <input
                    type="radio"
                    id="radioGroup"
                    name="radioGroup"
                    value="pixelatedPhoto"
                  />
                  <div>
                    Pixelated Photo
                  </div>
                </div>

                <div className="l-flex u-align-center u-margin-bottom-large">
                  <input
                    type="radio"
                    id="radioGroup"
                    name="radioGroup"
                    value="photoOfPhoto"
                  />
                  <div>
                    Photo of a Photo
                  </div>
                </div>

                <div className="l-flex u-align-center u-margin-bottom">
                  <input
                    type="radio"
                    id="radioGroup"
                    name="radioGroup"
                    value="serialMismatch"
                  />
                  <div>
                    Serial Mismatch
                  </div>
                </div>

                <div className="l-flex u-align-center">
                  <input
                    type="radio"
                    id="radioGroup"
                    name="radioGroup"
                    value="other"
                  />
                  <input
                    style={{ width: '97%' }}
                    autoFocus
                    id="otherReason"
                    name="otherReason"
                    maxLength={500}
                    type="text"
                    className="c-input"
                    placeholder={'Other reason'}
                    value={otherReason}
                    disabled={recoveryRejectReason !== 'other'}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="u-margin-bottom-large">
            <label className="c-field__label">
              Preview Message to Requester
            </label>
            <div className={`read-only-textbox ${!recoveryRejectReason ? 'placeholder' : ''} l-min-height-200 u-width-100-percent`}>
              {recoveryRejectReason ? rejectionMessage : 'Select reason to preview the message...'}
            </div>
          </div>

          <div className="l-flex-end">
            <button
              className="c-btn-outline"
              onClick={() => {
                setRecoveryRejectReason('');
                handleCancelRejectRecovery();
              }}
            >
              Cancel
            </button>
            <LoaderButton
              onClick={() => {
                const reason =
                  recoveryRejectReason === 'other'
                    ? otherReason
                    : recoveryRejectReason;

                if (isProcessingRequest) {
                  processRequest(selectedRequest?.toolID ?? '', false, reason);
                } else {
                  closeRequest(selectedRequest?.toolID ?? '', reason);
                }
              }}
              className="c-btn c-btn-error u-margin-left"
              disabled={(recoveryRejectReason === 'other' && (otherReason || '').length === 0) || !recoveryRejectReason}
              isLoading={isLoading}
              text={isProcessingRequest ? "Deny Request" : "Close Request"}
              loadingText={isProcessingRequest ? "Denying" : "Closing"}
              icon={'fa fa-trash-alt'}
            />
          </div>
        </div>
      </Dialog>

      <ConfirmModal
        showConfirmModal={showDisputeTransferModal}
        closeModal={() => {
          setShowDisputeTransferModal(false);
          setRecoveryRequestWithDispute(null);
        }}
        onConfirm={transferDisputeInfo}
        titleModal="Transfer Dispute Status"
        textModal="Would you like to transfer the dispute status of the shop that previously owned this tool to the shop requesting the tool?"
        textConfirmButton="Transfer Dispute Status"
      />

    </main>
}

export default (props: ChildProps) => {
  const userContext = useUser();
  return <Dashboard userContext={userContext} {...props} />;
};
